// echoCodesLoader.js - Load and merge all echo code JSON files

import codesVg from './codes_echo_vg.json';
import codesVd from './codes_echo_vd.json';
import codesOreillettes from './codes_echo_oreillettes.json';
import codesValveMitrale from './codes_echo_valve_mitrale.json';
import codesValveTricuspide from './codes_echo_valve_tricuspide.json';
import codesValveAortique from './codes_echo_valve_aortique.json';
import codesValvePulmonaire from './codes_echo_valve_pulmonaire.json';
import codesGrosVaisseaux from './codes_echo_gros_vaisseaux.json';
import codesPericarde from './codes_echo_pericarde.json';
import codesDivers from './codes_echo_divers.json';
import { getModifiersForEnumSet } from './modifier-enums.ts';

/**
 * Mapping of code prefixes to category keys
 */
export const PREFIX_TO_CATEGORY = {
  'RV': 'vd',           // Right Ventricle
  'LV': 'vg',           // Left Ventricle (Ventricule Gauche)
  'MV': 'valve_mitrale',
  'AV': 'valve_aortique',
  'TV': 'valve_tricuspide',
  'PV': 'valve_pulmonaire',
  'LA': 'oreillettes',  // Left Atrium
  'RA': 'oreillettes',  // Right Atrium
  'AO': 'gros_vaisseaux', // Aorta
  'PA': 'gros_vaisseaux', // Pulmonary Artery
  'PE': 'pericarde',    // Pericardium
  'PL': 'divers',       // Pleural
  'CO': 'divers',       // Conclusions
  'VS': 'divers',       // Ventricular Septum
  'AS': 'oreillettes',  // Atrial Septum
};

/**
 * Search shortcuts - map user-friendly prefixes to category keys
 * Supports both English and French abbreviations
 */
export const SEARCH_PREFIX_TO_CATEGORY = {
  // English prefixes
  'LV': 'vg',           // Left Ventricle
  'RV': 'vd',           // Right Ventricle
  'MV': 'valve_mitrale',
  'AV': 'valve_aortique',
  'TV': 'valve_tricuspide',
  'PV': 'valve_pulmonaire',
  'LA': 'oreillettes',
  'RA': 'oreillettes',
  'AO': 'gros_vaisseaux',
  'PA': 'gros_vaisseaux',
  'PE': 'pericarde',
  'AS': 'oreillettes',
  'VS': 'divers',
  'PL': 'divers',
  'CO': 'divers',

  // French prefixes
  'VG': 'vg',           // Ventricule Gauche
  'VD': 'vd',           // Ventricule Droit
  'VM': 'valve_mitrale',
  'VA': 'valve_aortique',
  'VT': 'valve_tricuspide',
  'VP': 'valve_pulmonaire',
  'OG': 'oreillettes',  // Oreillette Gauche
  'OD': 'oreillettes',  // Oreillette Droite
  'SA': 'oreillettes',  // Septum Atrial (same as AS)
  'SIV': 'divers',      // Septum Inter-Ventriculaire
};

/**
 * Category display names (ordered as they should appear)
 */
export const CATEGORY_NAMES = {
  'vg': 'VG - Ventricule Gauche',
  'vd': 'VD - Ventricule Droit',
  'oreillettes': 'Oreillettes',
  'valve_mitrale': 'Valve Mitrale',
  'valve_tricuspide': 'Valve Tricuspide',
  'valve_aortique': 'Valve Aortique',
  'valve_pulmonaire': 'Valve Pulmonaire',
  'gros_vaisseaux': 'Gros Vaisseaux',
  'pericarde': 'Péricarde',
  'divers': 'Divers',
};

/**
 * Extract codes from a JSON structure that may have sections or be flat
 * @param {Object} jsonData - The imported JSON object
 * @param {string} category - The category key (vd, vg, etc.)
 * @returns {Array} Array of code objects with category metadata
 */
function extractCodesFromJson(jsonData, category) {
  const allCodes = [];

  // Get the first (and usually only) key from the JSON
  const firstKey = Object.keys(jsonData)[0];
  const data = jsonData[firstKey];

  if (!Array.isArray(data)) {
    return allCodes;
  }

  // Check if data has sections (like valve_mitrale, divers, oreillettes)
  const hasSections = data.length > 0 && data[0].section && data[0].codes;

  if (hasSections) {
    // Process sectioned structure
    data.forEach(sectionData => {
      const section = sectionData.section || '';
      const codes = sectionData.codes || [];

      codes.forEach(codeObj => {
        // Dynamically resolve supplement from modifierEnumType
        const supplement = codeObj.modifierEnumType
          ? Array.from(getModifiersForEnumSet(codeObj.modifierEnumType))
          : (codeObj.supplement || null);

        allCodes.push({
          code: codeObj.code,
          nom: codeObj.nom,
          supplement: supplement,
          modifierEnumType: codeObj.modifierEnumType || null,
          supplementType: codeObj.supplementType || null,
          numericConfig: codeObj.numericConfig || null,
          category: category,
          section: section
        });
      });
    });
  } else {
    // Process flat structure
    data.forEach(codeObj => {
      // Dynamically resolve supplement from modifierEnumType
      const supplement = codeObj.modifierEnumType
        ? Array.from(getModifiersForEnumSet(codeObj.modifierEnumType))
        : (codeObj.supplement || null);

      allCodes.push({
        code: codeObj.code,
        nom: codeObj.nom,
        supplement: supplement,
        modifierEnumType: codeObj.modifierEnumType || null,
        supplementType: codeObj.supplementType || null,
        numericConfig: codeObj.numericConfig || null,
        category: category,
        section: null
      });
    });
  }

  return allCodes;
}

/**
 * Load all echo codes from JSON files (in display order)
 * @returns {Array} Array of all codes with metadata
 */
export function loadAllEchoCodes() {
  const allCodes = [
    ...extractCodesFromJson(codesVg, 'vg'),
    ...extractCodesFromJson(codesVd, 'vd'),
    ...extractCodesFromJson(codesOreillettes, 'oreillettes'),
    ...extractCodesFromJson(codesValveMitrale, 'valve_mitrale'),
    ...extractCodesFromJson(codesValveTricuspide, 'valve_tricuspide'),
    ...extractCodesFromJson(codesValveAortique, 'valve_aortique'),
    ...extractCodesFromJson(codesValvePulmonaire, 'valve_pulmonaire'),
    ...extractCodesFromJson(codesGrosVaisseaux, 'gros_vaisseaux'),
    ...extractCodesFromJson(codesPericarde, 'pericarde'),
    ...extractCodesFromJson(codesDivers, 'divers'),
  ];

  return allCodes;
}

/**
 * Create a Map for quick code lookup
 * @returns {Map<string, Object>} Map of code -> code object
 */
export function createCodeLookupMap() {
  const codes = loadAllEchoCodes();
  const codeMap = new Map();

  codes.forEach(codeObj => {
    codeMap.set(codeObj.code, codeObj);
  });

  return codeMap;
}

/**
 * Get category for a code based on its prefix
 * @param {string} code - The code (e.g., "RV-0028")
 * @returns {string|null} Category key or null if not found
 */
export function getCategoryForCode(code) {
  if (!code || typeof code !== 'string') return null;

  const prefix = code.split('-')[0];
  return PREFIX_TO_CATEGORY[prefix] || null;
}

/**
 * Detect if search text starts with a category prefix
 * @param {string} searchText - The search input text
 * @returns {Object|null} Object with { prefix, category, displayName, remainingText } or null
 */
export function detectCategoryPrefix(searchText) {
  if (!searchText || typeof searchText !== 'string') return null;

  const trimmed = searchText.trim();
  if (trimmed.length === 0) return null;

  // Try to match prefixes (case-insensitive)
  // Look for prefix at the start, followed by space or end of string
  const words = trimmed.split(/\s+/);
  const firstWord = words[0].toUpperCase();

  // Check if first word matches a search prefix
  const category = SEARCH_PREFIX_TO_CATEGORY[firstWord];

  if (category) {
    // Get remaining text (everything after the prefix)
    const remainingText = words.slice(1).join(' ').trim();

    return {
      prefix: firstWord,
      category: category,
      displayName: CATEGORY_NAMES[category],
      remainingText: remainingText
    };
  }

  return null;
}

/**
 * Validate if a code exists in the loaded JSON files
 * @param {string} code - The code to validate
 * @returns {boolean} True if code exists
 */
export function isValidCode(code) {
  const codeMap = createCodeLookupMap();
  return codeMap.has(code);
}

/**
 * Get full code details
 * @param {string} code - The code to look up
 * @returns {Object|null} Code object or null if not found
 */
export function getCodeDetails(code) {
  const codeMap = createCodeLookupMap();
  return codeMap.get(code) || null;
}

/**
 * Search codes by text (searches both code and nom fields)
 * @param {string} searchText - Text to search for
 * @param {string|null} categoryFilter - Optional category key to filter by
 * @returns {Array} Array of matching code objects
 */
export function searchCodes(searchText, categoryFilter = null) {
  if (!searchText || searchText.trim() === '') {
    return [];
  }

  const allCodes = loadAllEchoCodes();
  const searchLower = searchText.toLowerCase().trim();

  return allCodes.filter(codeObj => {
    // Filter by category if specified
    if (categoryFilter && codeObj.category !== categoryFilter) {
      return false;
    }

    // Search in code and nom fields
    const codeMatch = codeObj.code.toLowerCase().includes(searchLower);
    const nomMatch = codeObj.nom.toLowerCase().includes(searchLower);
    return codeMatch || nomMatch;
  });
}

/**
 * Get codes grouped by category
 * @returns {Object} Object with category keys and arrays of codes
 */
export function getCodesByCategory() {
  const allCodes = loadAllEchoCodes();
  const grouped = {};

  // Initialize all categories
  Object.keys(CATEGORY_NAMES).forEach(category => {
    grouped[category] = [];
  });

  // Group codes
  allCodes.forEach(codeObj => {
    if (grouped[codeObj.category]) {
      grouped[codeObj.category].push(codeObj);
    }
  });

  return grouped;
}

// Singleton instance for performance
let cachedCodeMap = null;

/**
 * Get cached code lookup map (for performance)
 * @returns {Map<string, Object>} Cached map
 */
export function getCachedCodeMap() {
  if (!cachedCodeMap) {
    cachedCodeMap = createCodeLookupMap();
  }
  return cachedCodeMap;
}

export default {
  loadAllEchoCodes,
  createCodeLookupMap,
  getCategoryForCode,
  detectCategoryPrefix,
  isValidCode,
  getCodeDetails,
  searchCodes,
  getCodesByCategory,
  getCachedCodeMap,
  PREFIX_TO_CATEGORY,
  SEARCH_PREFIX_TO_CATEGORY,
  CATEGORY_NAMES
};
